import React, { useCallback, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getUser, isAuthorized } from "../../helpers/sessionStorageHelper";
import { isApiPending } from "../../dux/apiStatus";
import {
  fetchPaymentReconciliationReport,
  runPaymentReconciliation,
  clearPaymentReconciliationError,
  selectPaymentReconciliationScan,
  selectPaymentReconciliationError,
} from "../../dux/paymentReconciliation";
import { openSnackbar } from "../../dux/snackbar";
import {
  formatInrFromMinor,
  getIssueKindLabel,
} from "../../helpers/paymentReconciliationHelper";
import { snackBarStatus } from "../../helpers/constants";
import styles from "./PaymentReconciliationPage.module.css";

const BOOKING_STATUS_OPTIONS = [
  "IN_PROGRESS",
  "PAYMENT_PENDING",
  "PAYMENT_FAILED",
  "CONFIRMED",
  "COMPLETED",
  "CANCELLED",
];

function getAllowedLocations() {
  const user = getUser();
  return user && user.locations ? user.locations : [];
}

const locationDisplay = {
  srinagar: "Srinagar",
  jammu: "Jammu",
};

function rowKey(issue, index) {
  return `${issue.bookingId ?? ""}-${issue.orderId ?? ""}-${index}`;
}

const PaymentReconciliationPage = () => {
  const dispatch = useDispatch();
  const allowedLocations = useMemo(() => getAllowedLocations(), []);
  const scanResult = useSelector(selectPaymentReconciliationScan);
  const pageError = useSelector(selectPaymentReconciliationError);

  const loadingReport = useSelector((s) =>
    isApiPending(s, "paymentReconciliationReport")
  );
  const loadingRun = useSelector((s) =>
    isApiPending(s, "paymentReconciliationRun")
  );
  const loading = loadingReport || loadingRun;

  const [limit, setLimit] = useState(100);
  const [createdAfter, setCreatedAfter] = useState(() => dayjs().subtract(30, "day"));
  const [location, setLocation] = useState(
    () => allowedLocations[0] || ""
  );
  const [bookingStatuses, setBookingStatuses] = useState(["IN_PROGRESS"]);
  const [remediateDialogOpen, setRemediateDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const filterPayload = useMemo(
    () => ({
      limit,
      created_after: createdAfter,
      location: location || undefined,
      booking_statuses: bookingStatuses.length
        ? bookingStatuses.join(",")
        : undefined,
    }),
    [limit, createdAfter, location, bookingStatuses]
  );

  const handleRunReport = useCallback(() => {
    dispatch(clearPaymentReconciliationError());
    dispatch(fetchPaymentReconciliationReport(filterPayload));
  }, [dispatch, filterPayload]);

  const handleRunRemediate = useCallback(() => {
    setRemediateDialogOpen(false);
    dispatch(clearPaymentReconciliationError());
    dispatch(runPaymentReconciliation(filterPayload));
  }, [dispatch, filterPayload]);

  const toggleExpand = useCallback((key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const copyOrderId = useCallback(
    (orderId) => {
      if (!orderId) return;
      const text = String(orderId);
      navigator.clipboard.writeText(text).then(
        () => {
          dispatch(
            openSnackbar({
              message: "Order id copied for Razorpay dashboard search.",
            })
          );
        },
        () => {
          dispatch(
            openSnackbar({
              message: "Could not copy to clipboard.",
              status: snackBarStatus.ERROR,
            })
          );
        }
      );
    },
    [dispatch]
  );

  const issues = scanResult?.issues ?? [];
  const hasIssues = issues.length > 0;
  const summary = scanResult?.summary;
  const remediatedIds = scanResult?.remediated_booking_ids ?? [];
  const remediationErrors = scanResult?.remediation_errors ?? [];
  const showRemediationSummary =
    remediatedIds.length > 0 || remediationErrors.length > 0;

  const summarySeverity = hasIssues ? "warning" : "info";
  const cardsVariant = hasIssues ? "outlined" : "elevation";

  if (!isAuthorized("SUPER_ADMIN")) {
    return <Navigate to="/404" replace />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className={styles.page}>
        <Typography variant="h4" component="h1" className={styles.title}>
          Payment reconciliation
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          Compare gateway payments with booking records. Remediation runs the
          same server-side payment sync used when checking a booking.
        </Typography>

        {allowedLocations.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No locations are assigned to your account. You need admin access
            with at least one location to run reconciliation.
          </Alert>
        )}

        {pageError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => dispatch(clearPaymentReconciliationError())}
          >
            {pageError}
          </Alert>
        )}

        <Card variant="outlined" className={styles.filters}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Limit"
                  type="number"
                  fullWidth
                  size="small"
                  value={limit}
                  onChange={(e) =>
                    setLimit(
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Created after"
                  value={createdAfter}
                  onChange={(v) => v && setCreatedAfter(v)}
                  slotProps={{
                    textField: { fullWidth: true, size: "small" },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Location"
                  fullWidth
                  size="small"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  disabled={allowedLocations.length === 0}
                >
                  {allowedLocations.map((loc) => (
                    <MenuItem key={loc} value={loc}>
                      {locationDisplay[loc] || loc}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="booking-statuses-label">
                    Booking statuses
                  </InputLabel>
                  <Select
                    labelId="booking-statuses-label"
                    multiple
                    value={bookingStatuses}
                    onChange={(e) => setBookingStatuses(e.target.value)}
                    input={<OutlinedInput label="Booking statuses" />}
                    renderValue={(selected) => selected.join(", ")}
                  >
                    {BOOKING_STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Default is IN_PROGRESS. Multi-select; sent as comma-separated
                  to the API.
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <div className={styles.actions}>
          <Button
            variant="contained"
            onClick={handleRunReport}
            disabled={loading || !location}
          >
            Run report
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => setRemediateDialogOpen(true)}
            disabled={loading || !location}
          >
            Run &amp; remediate
          </Button>
        </div>

        {showRemediationSummary && scanResult && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Last remediation result
            </Typography>
            {remediatedIds.length > 0 && (
              <Typography variant="body2" component="div">
                Updated bookings: {remediatedIds.join(", ")}
              </Typography>
            )}
            {remediationErrors.length > 0 && (
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                {remediationErrors.map((err, i) => {
                  const bid = err.booking_id ?? err.bookingId;
                  return (
                    <li key={`${bid}-${i}`}>
                      <Typography variant="body2">
                        {bid}: {err.error}
                      </Typography>
                    </li>
                  );
                })}
              </Box>
            )}
            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={handleRunReport}
              disabled={loading}
            >
              Run report again
            </Button>
          </Alert>
        )}

        <Grid container spacing={2} className={styles.cards}>
          {loading ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Skeleton variant="rounded" height={88} />
                </Grid>
              ))}
            </>
          ) : (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant={cardsVariant}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      Scanned
                    </Typography>
                    <Typography variant="h5">
                      {scanResult?.scanned_count ?? "—"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant={cardsVariant}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      Missing provider payment id
                    </Typography>
                    <Typography variant="h5">
                      {summary?.missing_provider_payment_id ?? "—"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant={cardsVariant}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      Status drift
                    </Typography>
                    <Typography variant="h5">
                      {summary?.status_drift ?? "—"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant={cardsVariant}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      Transaction id mismatch
                    </Typography>
                    <Typography variant="h5">
                      {summary?.transaction_id_mismatch ?? "—"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
        </Grid>

        {hasIssues && scanResult && !loading && (
          <Alert severity={summarySeverity} sx={{ mb: 2 }}>
            This scan found {issues.length} mismatch
            {issues.length === 1 ? "" : "es"}. Review the table below.
          </Alert>
        )}

        {scanResult && !loading && !hasIssues && (
          <Alert severity="success" sx={{ mb: 2 }}>
            No mismatches in this scan.
          </Alert>
        )}

        <Typography variant="h6" gutterBottom>
          Issues
        </Typography>
        <TableContainer
          className={styles.tableWrap}
          component={Card}
          variant="outlined"
          sx={{ overflowX: "auto" }}
        >
          {loading ? (
            <Box sx={{ p: 2 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rounded" height={40} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Booking</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>DB status</TableCell>
                  <TableCell>DB transaction</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Suggested provider payment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issues.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary">
                        {scanResult
                          ? "No rows — scan returned zero issues."
                          : "Run a report to load issues."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  issues.map((issue, index) => {
                    const rk = rowKey(issue, index);
                    const isOpen = expanded.has(rk);
                    const bookingId = issue.bookingId ?? issue.booking_id;
                    const loc = issue.location ?? "";
                    const recordsLink =
                      bookingId && loc
                        ? `/booking-records?booking_id=${encodeURIComponent(
                            bookingId
                          )}&location=${encodeURIComponent(loc)}`
                        : bookingId
                        ? `/booking-records?booking_id=${encodeURIComponent(
                            bookingId
                          )}`
                        : null;
                    return (
                      <React.Fragment key={rk}>
                        <TableRow hover>
                          <TableCell padding="checkbox">
                            <IconButton
                              size="small"
                              aria-expanded={isOpen}
                              aria-label={
                                isOpen ? "Collapse details" : "Expand details"
                              }
                              onClick={() => toggleExpand(rk)}
                            >
                              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            {recordsLink ? (
                              <Link
                                to={recordsLink}
                                className={styles.bookingLink}
                              >
                                {bookingId}
                              </Link>
                            ) : (
                              bookingId ?? "—"
                            )}
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Typography variant="body2" component="span">
                                {issue.orderId ?? "—"}
                              </Typography>
                              {issue.orderId ? (
                                <Tooltip title="Copy order id (Razorpay search)">
                                  <IconButton
                                    size="small"
                                    onClick={() => copyOrderId(issue.orderId)}
                                    aria-label="Copy order id"
                                  >
                                    <ContentCopyIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </Box>
                          </TableCell>
                          <TableCell>{loc || "—"}</TableCell>
                          <TableCell>{issue.dbBookingStatus ?? "—"}</TableCell>
                          <TableCell>{issue.dbTransactionId ?? "—"}</TableCell>
                          <TableCell>
                            {getIssueKindLabel(issue.issueKind)}
                          </TableCell>
                          <TableCell>
                            {issue.suggestedProviderPaymentId ?? "—"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            sx={{
                              py: 0,
                              borderBottom: isOpen ? undefined : 0,
                            }}
                          >
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Reasons
                                </Typography>
                                {Array.isArray(issue.reasons) &&
                                issue.reasons.length > 0 ? (
                                  <Box component="ul" sx={{ mt: 0, pl: 2 }}>
                                    {issue.reasons.map((r, ri) => (
                                      <li key={ri}>
                                        <Typography variant="body2">{r}</Typography>
                                      </li>
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No reasons listed.
                                  </Typography>
                                )}
                                <Typography
                                  variant="subtitle2"
                                  gutterBottom
                                  sx={{ mt: 2 }}
                                >
                                  Provider payments
                                </Typography>
                                {Array.isArray(issue.providerPaymentsSummary) &&
                                issue.providerPaymentsSummary.length > 0 ? (
                                  <Table size="small" sx={{ maxWidth: 480 }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Id</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Amount</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {issue.providerPaymentsSummary.map(
                                        (p, pi) => (
                                          <TableRow key={pi}>
                                            <TableCell>
                                              {p.providerPaymentId ?? p.id ?? "—"}
                                            </TableCell>
                                            <TableCell>
                                              {p.normalizedStatus ?? p.status ?? "—"}
                                            </TableCell>
                                            <TableCell>
                                              {formatInrFromMinor(
                                                p.amountMinor ?? p.amount
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No provider payment rows.
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <Dialog
          open={remediateDialogOpen}
          onClose={() => setRemediateDialogOpen(false)}
          aria-labelledby="remediate-dialog-title"
          aria-describedby="remediate-dialog-description"
        >
          <DialogTitle id="remediate-dialog-title">
            Run and remediate?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="remediate-dialog-description">
              This will update bookings on the server using the payment sync
              pipeline (same logic as checking a booking against the gateway).
              Only continue if you understand that database booking and payment
              fields may change.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemediateDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRunRemediate}
              color="warning"
              variant="contained"
              autoFocus
            >
              Confirm remediation
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </LocalizationProvider>
  );
};

export default PaymentReconciliationPage;
